import { apiFetch, API_BASE_URL, ApiError, classifiedFetch, type TokenStore } from './client.ts';

// ── Request types ─────────────────────────────────────────────────────────────

export interface LoginBody {
  email: string;
  password: string;
}

export interface RegisterBody {
  email: string;
  password: string;
  displayName: string;
}

export interface RequestPasswordResetBody {
  email: string;
}

export interface ConfirmPasswordResetBody {
  token: string;
  newPassword: string;
}

export interface VerifyMfaBody {
  code: string;
}

// ── Response types ────────────────────────────────────────────────────────────

export interface LoginResponse {
  accessToken: string;
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  /** Whether the user has verified their email (v7.26). */
  emailVerified: boolean;
}

export interface RegisterResponse {
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  emailVerified: boolean;
  createdAt: number;
}

export interface SessionSummary {
  id: string;
  createdAt: number;
  lastUsedAt: number;
  userAgent: string | null;
  ip: string | null;
}

// ── API functions ─────────────────────────────────────────────────────────────

/**
 * POST /auth/login - unauthenticated; no store needed.
 * The refresh cookie is set by the server via Set-Cookie (httpOnly+Secure).
 * The caller must store `accessToken` in React state - never in web storage.
 */
export async function login(body: LoginBody): Promise<LoginResponse> {
  const res = await classifiedFetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? 'LOGIN_FAILED', err.message ?? 'Login failed');
  }
  return res.json() as Promise<LoginResponse>;
}

export async function register(body: RegisterBody): Promise<RegisterResponse> {
  const res = await classifiedFetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? 'REGISTER_FAILED', err.message ?? 'Registration failed');
  }
  return res.json() as Promise<RegisterResponse>;
}

export async function requestPasswordReset(body: RequestPasswordResetBody): Promise<void> {
  const res = await classifiedFetch(`${API_BASE_URL}/auth/password-reset/request`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // Unknown email still 200s (user-enumeration prevention); only real failures throw.
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? 'RESET_REQUEST_FAILED', err.message ?? 'Password reset request failed');
  }
}

export async function confirmPasswordReset(body: ConfirmPasswordResetBody): Promise<{ readonly email: string }> {
  const res = await classifiedFetch(`${API_BASE_URL}/auth/password-reset/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? 'RESET_FAILED', err.message ?? 'Password reset failed');
  }
  return (await res.json()) as { email: string };
}

export async function logout(store: TokenStore): Promise<void> {
  await apiFetch<{ success: boolean }>('/auth/logout', { method: 'POST' }, store);
  store.onAuthFailure(); // clears in-memory token
}

export interface EnrollMfaResponse {
  factorId: string;
  secret: string;
  factorType: 'totp';
  /** User email for the otpauth:// URI account label - distinguishes entries in authenticator apps. */
  email: string;
}

/**
 * Establish the pending TOTP factor and get its secret for the QR code.
 * Idempotent while pending: calling it again (reload, second tab, StrictMode)
 * returns the SAME secret instead of rotating it, so every open surface shows
 * an identical QR. Recovery codes come back from `verifyMfa`, not from here.
 *
 * `replace` must be true to rotate an authenticator that ALREADY WORKS; without
 * it the server answers 409 MFA_ALREADY_ENROLLED rather than destroying a
 * factor the user is signing in with. Pass it only from an explicit user
 * confirmation - never from a mount. A replace additionally requires `mfaCode`
 * (current TOTP or recovery code) or the server answers 401 MFA_REQUIRED,
 * surfaced inline via suppressMfaRedirect.
 */
export async function enrollMfa(
  store: TokenStore,
  replace = false,
  mfaCode?: string,
): Promise<EnrollMfaResponse> {
  return apiFetch<EnrollMfaResponse>(
    '/auth/mfa/enroll',
    {
      method: 'POST',
      body: JSON.stringify(
        mfaCode === undefined
          ? { factorType: 'totp', replace }
          : { factorType: 'totp', replace, mfaCode },
      ),
    },
    store,
    { suppressMfaRedirect: true },
  );
}

export interface VerifyMfaResponse {
  verified: boolean;
  /** True when a single-use recovery code (not a TOTP step) satisfied MFA. */
  usedRecoveryCode: boolean;
  /** Unused recovery codes left - present only on the recovery branch. */
  recoveryCodesRemaining?: number;
  /** A freshly issued recovery sheet, present on the verify that activates a
   *  factor (and on the legacy backfill). Must be shown before proceeding -
   *  this response is the only place the plaintexts ever exist. */
  recoveryCodes?: string[];
}

export async function verifyMfa(body: VerifyMfaBody, store: TokenStore): Promise<VerifyMfaResponse> {
  return apiFetch<VerifyMfaResponse>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify(body),
  }, store);
}

/** Issue a fresh recovery-code sheet (invalidates all unused codes). Requires
 *  an MFA-verified session - the server refuses otherwise. */
export async function regenerateRecoveryCodes(store: TokenStore): Promise<string[]> {
  const data = await apiFetch<{ recoveryCodes: string[] }>(
    '/auth/mfa/recovery-codes/regenerate',
    { method: 'POST' },
    store,
  );
  return data.recoveryCodes;
}

/**
 * Email-OTP enrolment (universal-MFA): the low-friction second factor for
 * client/tutor personas. `sendEmailOtp` emails a fresh 6-digit code;
 * `verifyEmailOtp` confirms it and stamps the session like verifyMfa.
 */
/**
 * Request an email one-time code. Pass `resend: true` ONLY for a send the user
 * explicitly asked for - that rotates the code, invalidating any already in
 * their inbox. An automatic send (this screen mounting) must leave an
 * unexpired code alone, so it resolves `reused: true` and queues no email.
 */
export async function sendEmailOtp(
  store: TokenStore,
  resend = false,
): Promise<{ reused: boolean }> {
  const data = await apiFetch<{ sent: boolean; reused: boolean }>(
    '/auth/mfa/email-otp/send',
    { method: 'POST', body: JSON.stringify({ resend }) },
    store,
  );
  return { reused: data.reused };
}

export async function verifyEmailOtp(code: string, store: TokenStore): Promise<void> {
  await apiFetch<{ verified: boolean }>('/auth/mfa/email-otp/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }, store);
}

export async function listSessions(store: TokenStore): Promise<SessionSummary[]> {
  const data = await apiFetch<{ sessions: SessionSummary[] }>('/auth/sessions', {}, store);
  return data.sessions;
}

export async function revokeSession(sessionId: string, store: TokenStore): Promise<void> {
  await apiFetch<{ success: boolean }>(`/auth/sessions/${sessionId}`, { method: 'DELETE' }, store);
}

export interface RedeemInvitationResponse {
  practiceId: string;
  role: string;
}

export interface RedeemPreviewResponse {
  role: string;
  practiceName: string;
  makesCaseLead?: boolean;
  acknowledgements: { scope: 'practice' | 'case'; version: string; text: string }[];
}

/** GET /invitations/redeem-preview - reads only; same token error codes as redeem. */
export async function previewInvitationRedemption(
  code: string,
  store: TokenStore,
): Promise<RedeemPreviewResponse> {
  return apiFetch<RedeemPreviewResponse>(
    `/invitations/redeem-preview?code=${encodeURIComponent(code)}`,
    {},
    store,
  );
}

/**
 * POST /invitations/redeem - requires an authenticated user (requireAuth). The server
 * refuses a redemption that posts no current acknowledgement version (ToS 4.2).
 * Error codes: TOKEN_USED / TOKEN_EXPIRED → 410; TOKEN_NOT_FOUND → 404.
 */
export async function redeemInvitation(
  code: string,
  acknowledgementVersion: string,
  store: TokenStore,
): Promise<RedeemInvitationResponse> {
  return apiFetch<RedeemInvitationResponse>(
    '/invitations/redeem',
    { method: 'POST', body: JSON.stringify({ code, acknowledgementVersion }) },
    store,
  );
}

// ── Email verification (v7.26) ─────────────────────────────────────────────────

export interface VerifyEmailResponse {
  verified: boolean;
  alreadyVerified: boolean;
}

/**
 * POST /auth/verify-email - unauthenticated; the token is the credential, so
 * the link works whether or not the clicker is logged in (no store needed).
 * Error codes: EMAIL_VERIFICATION_TOKEN_EXPIRED → 410; *_INVALID → 400.
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const res = await classifiedFetch(`${API_BASE_URL}/auth/verify-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? 'VERIFY_EMAIL_FAILED', err.message ?? 'Verification failed');
  }
  return res.json() as Promise<VerifyEmailResponse>;
}

/**
 * POST /auth/verify-email/resend - authenticated; re-sends the verification
 * email for the current user. Rate-limited 3/h/IP. Returns { alreadyVerified }
 * as a no-op when the account is already verified.
 */
export async function resendEmailVerification(
  store: TokenStore,
): Promise<{ sent: boolean; alreadyVerified: boolean }> {
  return apiFetch<{ sent: boolean; alreadyVerified: boolean }>(
    '/auth/verify-email/resend',
    { method: 'POST' },
    store,
  );
}
